import { useState, useEffect, useCallback } from 'react';
import { getClients, createClient, deleteClient } from '../api/client';
import type { Client, CreateClientInput } from '../types';

interface UseClientsReturn {
  clients: Client[];
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  addClient: (input: CreateClientInput) => Promise<Client>;
  removeClient: (id: string) => Promise<void>;
}

export function useClients(): UseClientsReturn {
  const [clients, setClients] = useState<Client[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchClients = useCallback(async (): Promise<void> => {
    const token = localStorage.getItem('ptai_token');
    if (!token) return;
    setIsLoading(true);
    setError(null);
    try {
      const data = await getClients();
      setClients(data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch clients';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  const addClient = useCallback(async (input: CreateClientInput): Promise<Client> => {
    setIsLoading(true);
    setError(null);
    try {
      const client = await createClient(input);
      setClients(prev => [client, ...prev]);
      return client;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create client';
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const removeClient = useCallback(async (id: string): Promise<void> => {
    setIsLoading(true);
    setError(null);
    try {
      await deleteClient(id);
      setClients(prev => prev.filter(c => c.id !== id));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete client';
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    clients,
    isLoading,
    error,
    refresh: fetchClients,
    addClient,
    removeClient,
  };
}
